import Alpine from "alpinejs";
import focus from "@alpinejs/focus";
import mask from "@alpinejs/mask";
import persist from "@alpinejs/persist";
import axios from "axios";

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const PHONE_REGEX = /^(\+42[01])? ?\d{3} ?\d{3} ?\d{3}$/;

function getPathValue(source, path, defaultValue = undefined) {
  if (!path) {
    return source;
  }

  const value = path.split(".").reduce((current, segment) => {
    if (current == null) {
      return undefined;
    }

    return current[segment];
  }, source);

  return value === undefined ? defaultValue : value;
}

function setPathValue(source, path, value) {
  const segments = path.split(".");
  const lastSegment = segments.pop();

  if (!lastSegment) {
    return source;
  }

  const target = segments.reduce((current, segment) => {
    if (current[segment] == null || typeof current[segment] !== "object") {
      current[segment] = {};
    }

    return current[segment];
  }, source);

  target[lastSegment] = value;
  return source;
}

function cloneValue(source) {
  return JSON.parse(JSON.stringify(source || {}));
}

function countErrors(errors) {
  return Object.values(errors || {}).reduce((total, fields) => {
    return total + Object.values(fields || {}).reduce((fieldTotal, errorList) => {
      return fieldTotal + ((errorList || []).length || 0);
    }, 0);
  }, 0);
}

function getRegionalName(item, entityType) {
  const entity = (item.regionalStructure || []).find((value) => value.type === entityType);
  return entity ? entity.name : "";
}

function buildSuggestionOption(item) {
  const suggestFirstRow = item.name || "";
  const municipality =
    getRegionalName(item, "regional.municipality") ||
    getRegionalName(item, "regional.municipality_part");
  const country = getRegionalName(item, "regional.country");
  const locationParts = [item.label, item.location].filter(Boolean);
  return {
    label: [suggestFirstRow, locationParts.join(", ")].filter(Boolean).join(", "),
    category: item.type,
    payload: {
      suggestFirstRow,
      zipCode: item.zip || "",
      municipality,
      country,
      latitude: item.position ? item.position.lat : null,
      longitude: item.position ? item.position.lon : null,
    },
  };
}

function createCheckoutAddressStore(config = {}) {
  const initialForms = cloneValue(config.initial_forms || config.initialForms);
  const shippingInitial = cloneValue(initialForms.shipping);
  const hasShipping = Object.keys(shippingInitial).length > 0;
  const initialValues = cloneValue(initialForms);

  function shouldRequireValidatedStreetSelection(values, requireValidatedStreetSelection, form) {
    if (!requireValidatedStreetSelection) {
      return false;
    }

    const currentStreet = String(getPathValue(values, `${form}.street`, "") || "").trim();
    const initialStreet = String(getPathValue(initialValues, `${form}.street`, "") || "").trim();
    const mapsSuggestion = String(
      getPathValue(values, `${form}.maps_suggestion`, "") || "",
    ).trim();

    return Boolean(currentStreet) && currentStreet !== initialStreet && !mapsSuggestion;
  }

  return {
    initialValues,
    values: initialForms,
    errors: cloneValue(config.errors),
    requiredFields: cloneValue(config.required_fields || config.requiredFields),
    streetOptions: Object.keys(initialForms).reduce((result, formName) => {
      if (Object.prototype.hasOwnProperty.call(initialForms[formName], "street")) {
        result[formName] = [];
      }
      return result;
    }, {}),
    backups: hasShipping ? { shipping: shippingInitial } : {},
    shipToBilling: hasShipping,
    api: config.api || {},
    messages: config.messages || {},
    requireValidatedStreetSelection: Boolean(config.requireValidatedStreetSelection),

    init() {
      if (this.shipToBilling && this.values.billing && this.values.shipping) {
        this.copyBillingToShipping();
      }
    },

    fieldErrors(fieldId) {
      return getPathValue(this.errors, fieldId, []);
    },

    hasErrors() {
      return countErrors(this.errors) > 0;
    },

    setError(form, field, errors) {
      if (!this.errors[form]) {
        this.errors[form] = {};
      }
      this.errors[form][field] = errors;
    },

    validateField(form, field) {
      const value = String(getPathValue(this.values, `${form}.${field}`, "") || "").trim();
      let errors = [];

      if (field === "email" && value && !EMAIL_REGEX.test(value)) {
        errors = [this.messages.invalidEmail];
      } else if (field === "phone" && value && !PHONE_REGEX.test(value)) {
        errors = [this.messages.invalidPhone];
      } else if (
        form === "company" &&
        field === "name" &&
        String(getPathValue(this.values, "company.company_number", "") || "").trim() &&
        !value
      ) {
        errors = [this.messages.companyNameRequired];
      } else if (
        form === "company" &&
        field === "company_number" &&
        String(getPathValue(this.values, "company.name", "") || "").trim() &&
        !value
      ) {
        errors = [this.messages.companyNumberRequired];
      } else if ((this.requiredFields[form] || []).includes(field) && !value) {
        errors = [this.messages.required];
      } else if (
        field === "street" &&
        shouldRequireValidatedStreetSelection(
          this.values,
          this.requireValidatedStreetSelection,
          form,
        )
      ) {
        errors = [this.messages.streetAddressRequired];
      }

      this.setError(form, field, errors);
      return errors;
    },

    clearStreetOptions(form) {
      if (this.streetOptions[form]) {
        this.streetOptions[form] = [];
      }
    },

    copyBillingToShipping() {
      if (!this.values.billing || !this.values.shipping) {
        return;
      }

      Object.keys(this.values.shipping).forEach((field) => {
        this.values.shipping[field] = getPathValue(
          this.values,
          `billing.${field}`,
          this.values.shipping[field],
        );
      });

      // Shipping mirrors billing, so its validation state must mirror billing
      // too. Otherwise a stale server-rendered shipping error (e.g. on a hidden
      // street field the user can never edit) keeps the submit button disabled.
      if (this.errors && this.errors.shipping) {
        const billingErrors = this.errors.billing || {};
        Object.keys(this.errors.shipping).forEach((field) => {
          this.errors.shipping[field] = (billingErrors[field] || []).slice();
        });
      }
    },

    restoreShippingFromBackup() {
      if (!this.values.shipping || !this.backups.shipping) {
        return;
      }

      this.values.shipping = cloneValue(this.backups.shipping);
    },

    setFieldValue(form, field, value, options = {}) {
      const { validate = false, requestSuggestions = false, syncShipping = true } = options;

      if (!this.values[form]) {
        this.values[form] = {};
      }

      this.values[form][field] = value;

      if (["street", "postal_code", "city", "country"].includes(field)) {
        this.values[form].maps_suggestion = "";
        if (field === "street") {
          this.clearStreetOptions(form);
        }
      }

      if (
        syncShipping &&
        form === "billing" &&
        this.shipToBilling &&
        this.values.shipping &&
        Object.prototype.hasOwnProperty.call(this.values.shipping, field)
      ) {
        this.values.shipping[field] = value;
        if (["street", "postal_code", "city", "country"].includes(field)) {
          this.values.shipping.maps_suggestion = "";
        }
      }

      if (requestSuggestions && field === "street") {
        this.getData(value, form);
      }

      if (validate) {
        this.validateField(form, field);

        if (
          syncShipping &&
          form === "billing" &&
          this.shipToBilling &&
          this.values.shipping &&
          Object.prototype.hasOwnProperty.call(this.values.shipping, field)
        ) {
          this.validateField("shipping", field);
        }
      }

      return value;
    },

    onFieldChange(form, field, event) {
      this.setFieldValue(form, field, event.target.value, {
        validate: field !== "street",
        requestSuggestions: field === "street",
      });
    },

    onFieldBlur(form, field) {
      this.validateField(form, field);
    },

    selectOption(form, option) {
      const payload = option.payload || {};

      if (option.category === "regional.address") {
        this.setFieldValue(form, "street", payload.suggestFirstRow || "", { validate: false });
        this.setFieldValue(form, "postal_code", payload.zipCode || "", { validate: true });
        this.setFieldValue(form, "city", payload.municipality || "", { validate: true });
        this.setFieldValue(
          form,
          "country",
          payload.country || getPathValue(this.values, `${form}.country`, ""),
        );

        this.setFieldValue(form, "maps_suggestion", JSON.stringify(payload));
        this.clearStreetOptions(form);
        this.validateField(form, "street");
        return;
      }

      this.setFieldValue(form, "street", payload.suggestFirstRow || "", { validate: true });
      if (payload.municipality) {
        this.setFieldValue(form, "city", payload.municipality, { validate: false });
      }
      this.clearStreetOptions(form);
    },

    async getData(phrase, form) {
      const query = String(phrase || "").trim();
      if (!query) {
        this.clearStreetOptions(form);
        return;
      }

      if (!this.api.apiKey) {
        this.clearStreetOptions(form);
        return;
      }

      if (this.api.disabled) {
        this.clearStreetOptions(form);
        return;
      }

      try {
        const response = await window.axios.get(this.api.suggestUrl, {
          params: {
            apikey: this.api.apiKey,
            query,
            limit: 10,
            type: ["regional.address", "regional.street"],
            locality: [this.api.locality || "cz"],
            lang: this.api.lang || document.documentElement.lang || "cs",
          },
        });

        this.streetOptions[form] = (response?.data?.items || []).map(buildSuggestionOption);
      } catch (error) {
        if ([401, 403].includes(error?.response?.status)) {
          this.api.disabled = true;
          if (!this.api.warned) {
            this.api.warned = true;
            console.warn("Mapy autocomplete is disabled: invalid or unauthorized API key.");
          }
        }
        this.clearStreetOptions(form);
      }
    },

    onChangeSameAsBilling(event) {
      this.shipToBilling = !!event.target.checked;

      if (!this.values.shipping) {
        return;
      }

      if (this.shipToBilling) {
        this.backups.shipping = cloneValue(this.values.shipping);
        this.copyBillingToShipping();
      } else {
        this.restoreShippingFromBackup();
      }
    },
  };
}

function getAddressFieldErrors(fieldId) {
  const addressStore = window.Alpine && window.Alpine.store ? window.Alpine.store("address") : null;
  return addressStore && addressStore.fieldErrors ? addressStore.fieldErrors(fieldId) : [];
}

function startAlpine() {
  if (window.__zooshFrontAlpineStarted) {
    return;
  }

  window.__zooshFrontAlpineStarted = true;
  Alpine.start();
}

if (!window.__zooshFrontRuntimeInitialized) {
  window.__zooshFrontRuntimeInitialized = true;
  window.axios = axios;
  window._ = { get: getPathValue, set: setPathValue };
  window.Alpine = Alpine;
  window.getAddressFieldErrors = getAddressFieldErrors;
  window.ZooshCheckoutAddress = { createStore: createCheckoutAddressStore };

  Alpine.plugin(focus);
  Alpine.plugin(mask);
  Alpine.plugin(persist);

  window.__zooshStartAlpine = startAlpine;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startAlpine, { once: true });
  } else {
    queueMicrotask(startAlpine);
  }
}
